import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import type { IApiResponse } from "@/src/core/api/types";
import { chatService } from "../services";
import type { IChatPage, IMessage, ISingleConversation } from "../types";

const PAGE_SIZE = 50;

export const chatKeys = {
  inbox: ["chat", "inbox"] as const,
  messages: (id: string | number) => ["chat", "messages", String(id)] as const,
  conversation: (id: string | number) =>
    ["chat", "conversation", String(id)] as const,
};

/** Raw calls: conversation service, except find (Laravel). Throw an ApiError on failure. */
export const chatApi = {
  async getInbox(guest: number | string, page: number) {
    const params = querystring.stringify({ guest, page, per_page: PAGE_SIZE });
    const { data } = await api.conversation.get<IChatPage<ISingleConversation>>(
      `/conversation?${params}`,
    );
    return data;
  },

  async getMessages(id: string | number, page: number) {
    const params = querystring.stringify({ page, per_page: PAGE_SIZE });
    const { data } = await api.conversation.get<IChatPage<IMessage>>(
      `/conversation/${id}/messages?${params}`,
    );
    return data;
  },

  async getConversation(id: string | number) {
    const { data } = await api.conversation.get<
      IApiResponse<ISingleConversation>
    >(`/conversation/${id}`);
    return data.data;
  },

  /** Multipart, as the service expects; it then broadcasts new_message over the WS. */
  async sendMessage({ id, body }: { id: string | number; body: string }) {
    const form = new FormData();
    form.append("conversation_id", String(id));
    form.append("body", body);
    await api.conversation.post("/message", form);
  },

  async markAsRead(id: string | number) {
    await api.conversation.get(`/conversation/${id}/read`);
  },

  /** Fallback for a booking response without conversation_id; undefined when none is found. */
  async findConversationId(query: {
    host: number | string;
    guest: number | string;
    listing_id?: number | string;
  }): Promise<number | undefined> {
    try {
      const { data } = await api.laravel.get<
        IApiResponse<{ id: number } | null>
      >(`/conversation/find?${querystring.stringify(query)}`);
      return data.data?.id;
    } catch {
      return undefined;
    }
  },
};

export const useInboxQuery = (guest: number | string | undefined) =>
  useInfiniteQuery({
    queryKey: chatKeys.inbox,
    queryFn: ({ pageParam }) =>
      chatApi.getInbox(guest as number | string, pageParam),
    initialPageParam: 1,
    getNextPageParam: chatService.nextPage,
    enabled: guest !== undefined,
  });

/** Always fresh: the open thread is kept current by the WS, not the cache. */
export const useMessagesQuery = (id: string | number) =>
  useInfiniteQuery({
    queryKey: chatKeys.messages(id),
    queryFn: ({ pageParam }) => chatApi.getMessages(id, pageParam),
    initialPageParam: 1,
    getNextPageParam: chatService.nextPage,
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
    retry: 3,
    retryDelay: 1000,
  });

export const useConversationQuery = (id: string | number) =>
  useQuery({
    queryKey: chatKeys.conversation(id),
    queryFn: () => chatApi.getConversation(id),
    enabled: !!id,
  });

export const useSendMessageMutation = () =>
  useMutation({ mutationFn: chatApi.sendMessage });
